import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import SidebarLayout from '../../../../layouts/SidebarLayout';
import PageSubHeader from '../../../../components/Layouts/PageSubHeader';
import sidebar from '../../utils/sidebar';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline, IoCloudUploadOutline, IoTrashOutline } from 'react-icons/io5';
import { BsTrash3, BsDownload } from 'react-icons/bs';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { del, get, getBlob } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import DefaultButton from '../../../../components/Buttons/Default';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import { TfiFilter } from 'react-icons/tfi';
import FiltersDrawer from './Drawers/Filters';
import ImportModal from '../../../../components/Modals/Import';
import AgGridTable from '../../../../components/Tables/AgGrid';
import { printLeadStatus } from '../../utils/helpers';
import './styles.css';
import { useSettings } from '../../../../context/settings';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/crm/leads/edit/${data.id}`, { state: { leadData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setLeadToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function LeadsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const FILTER_KEYS = ['name', 'company_name', 'email', 'phone_number', 'type', 'source', 'status', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const getLeads = useCallback(async () => {
    setLoading(true);
    let query = `/crm/leads?page=${currentPage}&page_size=${pageSize}`;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.leads?.data) {
      setRowData(data.leads.data);
      setTotalPages(data.leads.last_page);
      setTotalRecords(data.leads.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getLeads();
  }, [getLeads]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  }

  const exportLeads = async () => {
    const data = await getBlob('/crm/leads/export')
      .then(blob => {
        if (!blob) throw new Error('export failed');
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads-${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(err => {
        toast.error(t('crm.exportError'));
      });
  }

  const deleteLead = async () => {
    const data = await del('/crm/leads/delete/' + leadToDelete);
    if (data?.success) {
      toast.success(data.message || t('crm.leadDeleted'));
      getLeads();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setLeadToDelete(null);
  };

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: t('crm.leadName'), 
      minWidth: 240, 
      flex: 1, 
      sortable: true, 
      onCellClicked: (params) => navigate(`/crm/leads/view/${params.data.id}`),
      cellRenderer: (params) => {
        return (
          <div>
            { printLeadStatus(params.data?.status) }
            <span>{' '} {params.value}</span>
          </div>
        );
      }
    },
    {
      field: 'phone_number', 
      headerName: t('common.phone'), 
      minWidth: 150, 
      flex: 1, 
      sortable: true,
      valueFormatter: (params) => {
        return (params.data.country_code ?? '') + ' ' + (params.value ?? '');
      }
    },
    { field: 'email', headerName: t('common.email'), minWidth: 250, flex: 1, sortable: true },
    { field: 'source', headerName: t('crm.source'), minWidth: 150, flex: 1, sortable: true },
    {
      field: 'status', 
      headerName: t('crm.expectedValue'), 
      minWidth: 150, 
      flex: 1, 
      sortable: true,
      cellRenderer: (params) => {
        if(!params.data.expected_value) return <span>-</span>;
        return <Badge color="primary">{params.data.expected_value} {params.data.currency?.toUpperCase()}</Badge>;
      }
    },
    { headerName: t('common.actions'), minWidth: 100, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer },
  ], [navigate, t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setLeadToDelete }), [navigate]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('crm.leads')} onReload={getLeads} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/crm/leads/create')}>
            {t('crm.newLead')}
          </DefaultButton>
          <Dropdown isOpen={moreOpen} toggle={() => setMoreOpen(o => !o)} size="sm">
            <DropdownToggle outline color="light">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => setImportModalOpen(true)}>
                <IoCloudUploadOutline size={15} />
                {t('common.import')}
              </DropdownItem>
              <DropdownItem onClick={exportLeads}>
                <BsDownload size={15} /> {t('common.export')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />
          
          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ImportModal 
        isOpen={importModalOpen}
        toggle={() => setImportModalOpen(false)}
        onImportSuccess={getLeads}
        module="crm"
        section="leads"
      />

      <ConfirmationModal
        isOpen={leadToDelete !== null}
        title={t('crm.deleteLead')}
        message={t('crm.deleteLeadConfirm')}
        onConfirm={deleteLead}
        toggle={() => setLeadToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default LeadsPage;
